export type IEnv = 'loc' | 'dev' | 'test' | 'prod';

export interface ILocation extends URL {
    top: string; // top level domain
    sld: string; // second level domain
    sub?: string; // subdomain
    env: IEnv;
    isEnv: (env: IEnv) => boolean;
    protocol: string;
    port: string;
}

/* for when you need url info, works in the client & for SSR */
export default function useUrl (): ILocation {
  const getLocationData = () => {
    return import.meta.server ? useRequestURL() : window.location
  }

  const getEnvironment = (location: Location): IEnv => {
    return (['loc', 'dev', 'test'].find(env => location.hostname.includes(`.${env}.`)) ||
            'prod') as IEnv
  }

  const getTLD = (hostname: string): string => {
    const parts = hostname.split('.')
    return parts.length > 1 ? (parts.at(-1) as string) : ''
  }

  const getSLD = (hostname: string): string => {
    const parts = hostname.split('.')
    return parts.length > 1 ? (parts.at(-2) as string) : hostname
  }

  const getSubdomain = (hostname: string): string | undefined => {
    const parts = hostname.split('.')
    return parts.length > 2 ? parts.at(0) : undefined
  }

  const isEnv = (envToCheck: IEnv): boolean => env === envToCheck

  const location = getLocationData()
  const env = getEnvironment(location)
  const defaultPort = location.port || (location.protocol === 'https' ? '443' : '80') // Default to 443 for https, 80 for http

  return {
    origin: location.origin,
    pathname: location.pathname,
    host: location.host,
    hostname: location.hostname,
    search: location.search,
    searchParams: location.searchParams, // only URL has searchParams
    hash: location.hash,
    env,
    top: getTLD(location.hostname),
    sld: getSLD(location.hostname),
    sub: getSubdomain(location.hostname),
    protocol: location.protocol.replace(':', ''),
    port: location.port || defaultPort,
    isEnv
  }
}
